import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Input from '@mui/material/InputBase';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type React from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_SIZE, OS_RESULT_WINDOW } from 'src/constants';
import { useLibrarySearchResults } from 'src/context/LibrarySearchResultsContext';
import viewStateVar from 'src/store/viewState';
import { pageRange } from 'src/utils/pagination';
import type { BaseSearchParams } from 'src/utils/searchSchemas';
import Pagination from './CustomPagination';

import styles from './HomePaginationStyle';

interface HomePaginationProps {
  showRecordCount?: boolean;
}

const isPositiveInt = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value > 0;

export default function HomePagination({ showRecordCount }: HomePaginationProps): React.ReactElement | null {
  const classes = styles();
  const _params = useParams({ strict: false });
  const navigate = useNavigate();
  // Get typed search params from TanStack Router - this is the source of truth for pagination
  const search = useSearch({ strict: false }) as BaseSearchParams;
  const { searchableCount } = useLibrarySearchResults();
  // Use search params as source of truth for page and perPage
  const page = search.page;
  const perPage = search.size;
  const [gotoPageValue, setGotoPageValue] = useState<string>('');
  const hasActiveFilters = (search.filters || []).length > 0 || !!search.term;

  // Provide defaults for undefined page/perPage (should rarely happen due to schema defaults)
  const effectivePage = isPositiveInt(page) ? page : 1;
  const effectivePerPage = isPositiveInt(perPage) ? perPage : DEFAULT_SIZE;
  const cappedTotal = searchableCount === null ? null : Math.min(searchableCount, OS_RESULT_WINDOW);

  const numberOfPages = useMemo(() => {
    if (cappedTotal === null || cappedTotal <= 0) return 0;
    return Math.ceil(cappedTotal / effectivePerPage);
  }, [cappedTotal, effectivePerPage]);

  const renderedPage = numberOfPages > 0 ? Math.min(effectivePage, numberOfPages) : effectivePage;
  const range = cappedTotal === null ? { from: 0, to: 0 } : pageRange(renderedPage, effectivePerPage, cappedTotal);

  useEffect(() => {
    if (searchableCount === null || numberOfPages <= 0) return;
    if (effectivePage <= numberOfPages) return;

    navigate({
      replace: true,
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        page: numberOfPages,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
  }, [effectivePage, navigate, numberOfPages, searchableCount]);

  const handlePagination = (
    _ev: React.MouseEvent | KeyboardEvent<HTMLInputElement> | undefined,
    pageValue: number,
  ): void => {
    const pageNum = Number(pageValue);
    if (!Number.isFinite(pageNum) || pageNum < 1) return;
    // Update URL via TanStack Router navigate
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        page: pageNum,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });

    // Update UI state (not URL state)
    viewStateVar({
      ...viewStateVar(),
      showHorizontal: false,
      horizontalMediaCardDetails: { image: '', title: '' },
      akomiMediaCard: [],
      horizontalMediaCardIndex: 0,
    });
  };

  if (cappedTotal === null || cappedTotal <= effectivePerPage || searchableCount === null) return null;

  return (
    <div className={classes.root} data-testid="home-pagination">
      <div
        style={{
          display: 'block',
          alignItems: 'center',
          marginTop: 20,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          className={classes.paginationWrapper}
        >
          <Pagination
            count={numberOfPages}
            page={renderedPage}
            onChange={(pageNum: number, e?: React.MouseEvent) => handlePagination(e, pageNum)}
            variant="outlined"
            shape="rounded"
            color="secondary"
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginLeft: '10px',
            }}
          >
            <Typography noWrap sx={{ color: 'text.secondary' }}>
              Go to page
            </Typography>
            <Input
              id="cyp-goto-page"
              onKeyDown={(k: KeyboardEvent<HTMLInputElement>) =>
                k.key === 'Enter' &&
                Number(gotoPageValue) <= numberOfPages &&
                Number(gotoPageValue) > 0 &&
                handlePagination(k, Number(gotoPageValue))
              }
              onChange={(e: ChangeEvent<HTMLInputElement>) => setGotoPageValue(e.target.value)}
              value={gotoPageValue}
              classes={{
                root: classes.inputRoot,
                input: classes.inputInput,
              }}
              sx={{
                color: 'text.primary',
                fontSize: 14,
              }}
            />
            <Button
              id="cyp-go-to-page-button"
              color="primary"
              disabled={
                gotoPageValue === '' ||
                Number.isNaN(Number(gotoPageValue)) ||
                Number(gotoPageValue) <= 0 ||
                Number(gotoPageValue) > numberOfPages
              }
              onClick={() => {
                const pageNum = Number(gotoPageValue);
                if (pageNum > 0 && pageNum <= numberOfPages) {
                  handlePagination(undefined, pageNum);
                  setGotoPageValue(''); // Clear input after navigation
                }
              }}
            >
              Go
            </Button>
          </div>
        </div>
      </div>
      {searchableCount < OS_RESULT_WINDOW ? (
        <Typography component="span" data-testid="pagination-range" sx={{ color: 'text.secondary', mt: '10px' }}>
          {showRecordCount &&
            `Showing ${range.from}-${range.to} of ${searchableCount}${hasActiveFilters ? ' (filtered)' : ''}`}
        </Typography>
      ) : (
        <Typography component="span" data-testid="pagination-range" sx={{ color: 'text.secondary', mt: '10px' }}>
          {showRecordCount &&
            `Showing ${range.from}-${range.to} of more than ${OS_RESULT_WINDOW}${hasActiveFilters ? ' (filtered)' : ''}`}
        </Typography>
      )}
    </div>
  );
}
